import {
    ButtonFlavor,
    ButtonSize,
    ButtonType,
    ExButton,
    ExEmptyState,
    ExIcon,
} from '@boomi/exosphere';
import type { ReactElement } from 'react';
import Loading from '../Loading';
import { translations } from '../translations/translations';
import AnimatedLink from './AnimatedLink';
import { useApp } from './AppProvider';
import TextWithArrows from './TextWithArrows';

import '../common/item-list.css';

interface Props<Item> {
    backTo?: string | null;
    title: string;
    hint?: string | null;
    toolbarFilters?: ReactElement | null;
    toolbarActions?: ReactElement | null;
    items: Item[];
    itemContent: (item: Item) => ReactElement;
    emptyStateLabel?: string;
    emptyStateText?: string;
    extraContent?: ReactElement | null;
}

const ItemList = <Item,>({
    backTo = null,
    title,
    hint = null,
    toolbarFilters = null,
    toolbarActions = null,
    items,
    itemContent,
    extraContent = null,
    emptyStateLabel = '',
    emptyStateText = '',
}: Props<Item>) => {
    const { language, isLoading } = useApp();

    return (
        <div className="page-layout">
            <section className="page-heading">
                {/* optional back button */}
                {backTo && (
                    <AnimatedLink to={backTo} tabIndex={-1} className="back-button">
                        <ExButton
                            className="exo-button"
                            flavor={ButtonFlavor.BASE}
                            size={ButtonSize.LARGE}
                            type={ButtonType.SECONDARY}
                        >
                            <ExIcon className="btn-icon" icon="Left arrow" hide-browser-tooltip />
                            &nbsp;
                            {translations[language].COMMON_back}
                        </ExButton>
                    </AnimatedLink>
                )}

                {/* page title */}
                <h3 className="title xxl">
                    <TextWithArrows text={title} />
                </h3>

                {/* optional info/hint text */}
                {hint && <p className="text md">{hint}</p>}
            </section>

            {/* optional toolbar */}
            {(toolbarFilters || toolbarActions) && (
                <section className="page-toolbar">
                    {/* optional search/select */}
                    {toolbarFilters && (
                        <section className="toolbar-filters">{toolbarFilters}</section>
                    )}
                    {/* optional actions */}
                    {toolbarActions && (
                        <section className="toolbar-actions">{toolbarActions}</section>
                    )}
                </section>
            )}

            {/* content/items list */}
            <section className="page-content">
                <ul className="content-list">
                    {items.length > 0 ? (
                        items.map((item) => itemContent(item))
                    ) : (
                        <ExEmptyState
                            className="empty-state"
                            label={emptyStateLabel || translations[language].COMMON_no_results}
                            text={emptyStateText}
                        >
                            <ExIcon className="empty-state-icon" slot="header" icon="community" />
                        </ExEmptyState>
                    )}
                </ul>
            </section>

            {/* optional extra content */}
            {extraContent && <section className="page-extra-content">{extraContent}</section>}

            {isLoading ? <Loading message={translations[language].COMMON_loading} /> : null}
        </div>
    );
};

export default ItemList;
